import React from 'react';
import { Box, Divider } from '@mui/material';
import { useAppointmentByIdQuery, usePatientCareTeamNextAppointmentsQuery, usePatientNextAppointmentsQuery, useUserMatchesQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { InCallUpcomingAppointments, InCallNoUpcomingSessions, InCallNoUpcomingSessionsWithCareTeam, InCallUpcomingAppointmentsWithCareTeam, Button } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';


export type UpcomingAppointmentsProps = {
  appointmentId: string;
  patientId: string;
  onScheduleSession: () => void;
};


const CareTeamSessions: React.VFC<{ patientId: string }> = (props) => {
  const me = useCurrentuser();
  const { data: matches } = useUserMatchesQuery(props.patientId, undefined, { suspense: true });
  const { data: _careTeamUpcomingAppointments } = usePatientCareTeamNextAppointmentsQuery(props.patientId, { suspense: true });
  const careTeamUpcomingAppointments = React.useMemo(() => {
    return _careTeamUpcomingAppointments?.filter(a => a.providerId !== me.id) ?? [];
  }, [ _careTeamUpcomingAppointments, me.id ]);

  if (matches.activeMatches?.every(m => m.provider.id === me.id)) {
    return null;
  }

  if (careTeamUpcomingAppointments.length > 0) {
    return (
      <InCallUpcomingAppointmentsWithCareTeam
        patientId={props.patientId}
        appointments={careTeamUpcomingAppointments}
      />
    );
  }

  return(
    <InCallNoUpcomingSessionsWithCareTeam patientId={props.patientId} />
  );
};


export const UpcomingAppointments: React.FC<UpcomingAppointmentsProps> = (props) => {
  const me = useCurrentuser();
  const { data: currentAppointment } = useAppointmentByIdQuery(props.appointmentId, { suspense: true });
  const { data: upcomingAppointments = [] } = usePatientNextAppointmentsQuery({
    patient_id: props.patientId,
    provider_id: me.id,
    dt_from: CalendarDateService.date(currentAppointment.fromDatetime).add(1, 'second').format(),
    appointments_per_series: 1,
    limit: 5,
  }, { suspense: true });

  const hasFooter = upcomingAppointments.length > 0;

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {upcomingAppointments.length ? (
        <InCallUpcomingAppointments
          patientId={props.patientId}
          nextSessions={upcomingAppointments}
          nextSessionId={upcomingAppointments?.[0]?.id}
          excludeAppointmentId={props.appointmentId}
        />
      ) : (
        <InCallNoUpcomingSessions
          patientId={props.patientId}
          onScheduleSession={props.onScheduleSession}
        />
      )}
      <Divider />
      <CareTeamSessions patientId={props.patientId} />
      <Box display="flex" flex={1} />
      {hasFooter && (
        <>
          <Divider />
          <Box p={3}>
            <Button color="secondary" variant="contained" fullWidth onClick={props.onScheduleSession}>Add session</Button>
          </Box>
        </>
      )}
    </Box>
  );
};
